/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import AmbassadorBadge from 'components/atoms/ambassador_badge';
import EmptyState from 'components/atoms/empty_state';
import PillButton from 'components/atoms/pill_button';
import {
  AmbassadorAnswer,
  ambassadorToolPath,
  findAmbassadorToolState,
  formatAmbassadorDate,
  isAwaitingRelease,
  sendAmbassadorFeedback,
} from 'services/ambassador';
import { AMBASSADOR_TOOLS, findAmbassadorTool } from 'services/ambassador_tools';
import { ApplicationState } from 'store';
import { refreshProfile } from 'store/modules/profile_store/actions';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';

// Mesmo limite validado no backend (Ambassador::Feedback::COMMENT_MAX_LENGTH).
const COMMENT_MAX_LENGTH = 2000;

const ANSWERS: { value: AmbassadorAnswer; label: string }[] = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'partially', label: 'Em parte' },
];

type Question = 'solved_need' | 'easy_to_use';

const QUESTIONS: { field: Question; label: string }[] = [
  { field: 'solved_need', label: 'A ferramenta resolveu o que você precisava?' },
  { field: 'easy_to_use', label: 'Foi fácil entender como usar?' },
];

const RATINGS = Array.from({ length: 11 }, (_, index) => index);

type SendStatus = 'idle' | 'sending' | 'sent' | 'error';

const AmbassadorToolFeedback: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { slug } = useParams<{ slug: string }>();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isAmbassador = Boolean(profile.ambassador?.member);
  const tool = findAmbassadorTool(slug);
  const toolState = findAmbassadorToolState(profile.ambassador?.tools, slug);
  const [nextToolMessage, setNextToolMessage] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Partial<Record<Question, AmbassadorAnswer>>>({});
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');

  useEffect(() => {
    document.title = tool ? `Feedback ${tool.name} - Embaixadores` : 'Embaixadores - ConLicitação';
  }, [tool]);

  if (!isAmbassador) return <Redirect to="/painel" />;

  if (!tool) {
    return (
      <Container className="page-container">
        <div className="container-fluid big-resolutions py-5 px-md-5">
          <EmptyState
            icon="search_off"
            title="Ferramenta não encontrada"
            description="Ela pode ter saído do programa de testes."
          />
        </div>
      </Container>
    );
  }

  // Feedback só depois de iniciar o teste; com o prazo encerrado, continua aberto.
  if (toolState.status !== 'testing' && toolState.status !== 'expired') {
    return <Redirect to={ambassadorToolPath(tool.slug)} />;
  }

  const { link } = tool;
  const { solved_need: solvedNeed, easy_to_use: easyToUse } = answers;
  const sending = sendStatus === 'sending';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!solvedNeed || !easyToUse || rating === null || sending) return;

    setSendStatus('sending');
    const sent = await sendAmbassadorFeedback({
      tool_slug: tool.slug,
      solved_need: solvedNeed,
      easy_to_use: easyToUse,
      rating,
      comment: comment.trim(),
    }).catch(() => false);
    setSendStatus(sent ? 'sent' : 'error');
    if (!sent) return;

    // O primeiro feedback libera a próxima ferramenta da sequência, se a data dela já chegou.
    const nextTool =
      AMBASSADOR_TOOLS[AMBASSADOR_TOOLS.findIndex((item) => item.slug === tool.slug) + 1];
    if (!toolState.feedback_sent && nextTool) {
      const nextState = findAmbassadorToolState(profile.ambassador?.tools, nextTool.slug);
      if (isAwaitingRelease(nextState) && nextState.available_on) {
        setNextToolMessage(
          `${nextTool.name} será liberada para teste em ${formatAmbassadorDate(
            nextState.available_on,
          )}.`,
        );
      } else if (nextTool.link) {
        setNextToolMessage(`${nextTool.name} já está liberada para teste na área do embaixador.`);
      }
    }
    dispatch(refreshProfile());
  };

  const sendAnother = () => {
    setAnswers({});
    setRating(null);
    setComment('');
    setSendStatus('idle');
  };

  return (
    <Container className="page-container">
      <div className="container-fluid big-resolutions py-5 px-md-5">
        <div className="feedback-card">
          <div className="feedback-heading">
            <h1>Feedback {tool.name}</h1>
            <AmbassadorBadge />
          </div>

          {sendStatus === 'sent' ? (
            <div className="feedback-sent" role="status">
              <i className="material-icons">check_circle</i>
              <strong>Feedback enviado!</strong>
              <p>Obrigado por compartilhar a sua experiência com a nossa equipe.</p>
              {nextToolMessage && <p>{nextToolMessage}</p>}
              <div className="feedback-sent-actions">
                <button type="button" className="btn btn-link" onClick={sendAnother}>
                  Enviar outro feedback
                </button>
                {toolState.status === 'testing' && link ? (
                  <PillButton onClick={() => history.push(link)}>
                    Voltar para a ferramenta
                  </PillButton>
                ) : (
                  <PillButton onClick={() => history.push('/painel')}>Ir para o painel</PillButton>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {QUESTIONS.map((question) => (
                <div
                  key={question.field}
                  className="feedback-section"
                  role="group"
                  aria-labelledby={`ambassador-feedback-${question.field}`}
                >
                  <p id={`ambassador-feedback-${question.field}`} className="feedback-question">
                    {question.label}
                  </p>
                  <div className="feedback-options">
                    {ANSWERS.map((answer) => (
                      <button
                        key={answer.value}
                        type="button"
                        className={answers[question.field] === answer.value ? 'selected' : ''}
                        aria-pressed={answers[question.field] === answer.value}
                        disabled={sending}
                        onClick={() =>
                          setAnswers((current) => ({ ...current, [question.field]: answer.value }))
                        }
                      >
                        {answer.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div
                className="feedback-section"
                role="group"
                aria-labelledby="ambassador-feedback-rating"
              >
                <p id="ambassador-feedback-rating" className="feedback-question">
                  Que nota você dá para a ferramenta?
                </p>
                <div className="feedback-scale">
                  <div className="feedback-options feedback-rating">
                    {RATINGS.map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={rating === value ? 'selected' : ''}
                        aria-pressed={rating === value}
                        disabled={sending}
                        onClick={() => setRating(value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                  <div className="feedback-scale-labels" aria-hidden="true">
                    <span>Muito ruim</span>
                    <span>Excelente</span>
                  </div>
                </div>
              </div>

              <div className="feedback-section feedback-comment">
                <label htmlFor="ambassador-feedback-comment">Compartilhe o seu feedback:</label>
                <textarea
                  id="ambassador-feedback-comment"
                  placeholder="Deixe o seu comentário"
                  value={comment}
                  maxLength={COMMENT_MAX_LENGTH}
                  disabled={sending}
                  onChange={(event) => setComment(event.target.value)}
                />
              </div>

              {sendStatus === 'error' && (
                <div className="alert alert-danger mt-3" role="alert">
                  Não foi possível enviar o seu feedback agora. Tente novamente.
                </div>
              )}

              <div className="feedback-submit">
                <PillButton
                  type="submit"
                  disabled={!solvedNeed || !easyToUse || rating === null || sending}
                >
                  {sending ? 'Enviando...' : 'Enviar feedback'}
                </PillButton>
              </div>
            </form>
          )}
        </div>

        {tool.directorName && (
          <div className="director-card">
            <div className="director-info">
              <span>Diretor responsável pelo projeto:</span>
              <strong>{tool.directorName}</strong>
            </div>
            {tool.directorWhatsappUrl && (
              <PillButton variant="dark" icon="whatsapp" href={tool.directorWhatsappUrl}>
                Falar sobre o projeto
              </PillButton>
            )}
          </div>
        )}
      </div>
    </Container>
  );
};

export default AmbassadorToolFeedback;
